// ─────────────────────────────────────────────
//  timelineCommit — the release-time decision for a dragged case bar.
//
//  PURE. No React, no Firestore. It answers one question: given where the bar
//  was let go, may this be written, and is there anything the board runner needs
//  told about it?
//
//  ⚠️ NOTHING HERE RE-IMPLEMENTS A RULE. The refusal comes from
//  `validateCaseTimes` (panelTimes.js:20) — the same validator `BoardCPage` uses
//  — and the warning comes from `doubleBookedProviders` (doubleBooking.js:52) —
//  the same detector that raises the NEEDS YOU NOW row. A second opinion is a
//  future disagreement: the drag would eventually refuse something the modal
//  allows, or stay silent about a clash the panel is shouting about.
//
//  ⚠️ REFUSAL vs WARNING IS NOT ARBITRARY.
//    · A same-room overlap REFUSES.   (the case cannot go there)
//    · A same-person, two-room clash WARNS.  (the case can go there; somebody
//      now has to sort the people out)
//  See the comment at the refusal site below for why the first of those does not
//  contradict `doubleBooking.js:70`.
// ─────────────────────────────────────────────

import { validateCaseTimes } from "./panelTimes";
import { doubleBookedProviders } from "./doubleBooking";
import { hmToMinutes, isCaselessWindow, crnaSeatKey, crnaSeatName } from "./timeline";

const arr = (v) => (Array.isArray(v) ? v : []);

// The dragged case's provider, keyed the way `buildTimeline` keys `placed`.
//
// ⚠️ THIS IS NOW THE SAME FUNCTION, NOT A MATCHING COPY. It used to re-derive
// the key as `ahpId || provider || ahpName`, with a comment promising that it
// matched `buildTimeline`. It did — until the seat rule changed there and not
// here, at which point a dragged orienting row keyed as the whole mashed cell
// while `placed` keyed as the preceptor: the same person, two keys, and the
// double-booking warning silently stopped firing for the one row that has two
// people standing in it. A promise to stay in sync is not a mechanism.
const keyOf = crnaSeatKey;
const nameOf = crnaSeatName;

// A room's other cases, as `validateCaseTimes` wants them.
//
// ⚠️ THE SYNTHETIC FULL-AXIS BAR MUST BE DROPPED. An assignment with a provider
// but no parseable start time is drawn by `buildTimeline` as an on-call watch
// spanning `axisStart`→`axisEnd` (timeline.js:675-703). Its `startMin`/`endMin`
// are the AXIS, not its data — and `validateCaseTimes` prefers a finite
// `startMin` over the (empty) string. Left in the list, that one row would
// overlap literally every position on the chart and make every case in its room
// permanently undraggable, with a refusal message quoting times nobody entered.
// `isCaselessWindow` is the shipped reading of "has no window"; the `onCall`
// flag keeps a merely-broken end time (which still draws a real bar) in the list.
function otherCasesIn(roomCases, caseRow) {
  const draggedId = caseRow ? caseRow.id : undefined;
  return arr(roomCases).filter((c) => {
    if (!c) return false;
    if (c === caseRow) return false;
    if (draggedId != null && c.id === draggedId) return false;
    // No id to go on: fall back to the window. Leaving the dragged case in its
    // own `otherCases` makes it overlap itself and refuses every drag.
    if (draggedId == null && isSameWindow(c, caseRow)) return false;
    if (c.onCall && isCaselessWindow(c)) return false;
    return true;
  });
}

const finite = (n) => typeof n === "number" && Number.isFinite(n);

function isSameWindow(a, b) {
  if (!a || !b) return false;
  if (!finite(a.startMin) || !finite(a.endMin)) return false;
  if (!finite(b.startMin) || !finite(b.endMin)) return false;
  if (a.roomId == null || b.roomId == null || a.roomId !== b.roomId) return false;
  return a.startMin === b.startMin && a.endMin === b.endMin;
}

// The board as it would be if this move were written, so the SHIPPED detector
// judges it (the `OutstandingFixModal.js:192` pattern) rather than a local
// overlap test written here.
// Only ever called with a caseRow that has a resolvable provider key — the
// caller checks that first, because a case with nobody in it cannot double-book.
function hypotheticalBoard(placed, caseRow, nextStartMin, nextEndMin) {
  const moved = {
    roomId: caseRow.roomId,
    startMin: nextStartMin,
    endMin: nextEndMin,
    crnaKey: keyOf(caseRow),
    crnaName: nameOf(caseRow) || null,
    id: caseRow.id,
  };

  // `placed` carries no assignment id (timeline.js pushes who-and-when only), so
  // the dragged entry is identified by id when the caller supplied one and by
  // its room + original window otherwise.
  const list = arr(placed);
  let replaced = false;
  const out = list.map((p) => {
    if (replaced || !p) return p;
    const sameById = p.id != null && caseRow.id != null && p.id === caseRow.id;
    if (sameById || isSameWindow(p, caseRow)) {
      replaced = true;
      return { ...p, ...moved };
    }
    return p;
  });
  // A board that does not contain the case being dragged is a caller bug, not a
  // reason to skip the check — append so the move is still judged. A duplicate
  // in the SAME room cannot itself raise a false clash: `doubleBooking.js:75`
  // skips same-room pairs by design.
  if (!replaced) out.push(moved);
  return out;
}

// A room pair, order-independent. `{a:"r1",b:"r4"}` and `{a:"r4",b:"r1"}` are
// the SAME clash, and comparing the raw objects would call the second one new.
//
// ⚠️ EXPORTED, because a second surface now asks the same before/after question
// (`domain/assignmentConflict.js` — the Add Assignment modal). This file's own
// header says a second opinion is a future disagreement; a second *copy* of the
// signature rule is the same bug one level down — it would re-announce a clash
// the drag already decided was old, or stay quiet about one it calls fresh.
export function pairSignature(pair) {
  return [pair.a, pair.b].map(String).sort().join("|");
}

export function pairKeys(conflicts, key) {
  const found = arr(conflicts).find((c) => c && c.key === key);
  const out = new Set();
  for (const p of arr(found && found.pairs)) {
    if (!p) continue;
    out.add(pairSignature(p));
  }
  return out;
}

// "OR 1", "OR 1 and OR 4", "OR 1, OR 4 and OR 7". Exported for the same reason
// as `pairSignature`: the modal's warning must read like the drag's warning, and
// two list-joining helpers drift into two different sentences about one board.
export function labelRooms(roomIds, roomNames) {
  const label = (id) => {
    if (roomNames && typeof roomNames.get === "function") return String(roomNames.get(id) || id);
    if (roomNames && typeof roomNames === "object" && roomNames[id]) return String(roomNames[id]);
    return String(id);
  };
  const names = arr(roomIds).map(label);
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * May this dragged case be written, and what should the runner be told?
 *
 * @param caseRow        the case being dragged — a `buildTimeline` case
 *                       ({ id, roomId, startMin, endMin, ahpId, provider, … })
 * @param roomCases      that room's cases; the dragged one is filtered out here
 *                       so no caller can forget to
 * @param nextStartTime  "HH:MM" — from `dragResult`
 * @param nextEndTime    "HH:MM" — from `dragResult`
 * @param placed         the board's flat cases (`buildTimeline().placed`), used
 *                       only for the double-booking WARNING
 * @param roomNames      optional { [roomId]: name } or Map, to name rooms in the
 *                       warning; ids are used when absent
 *
 * @returns { ok, error, warning, patch }
 *          ok:false → `error` is the validator's message VERBATIM, `patch` null.
 *          ok:true  → `patch` is { startTime, endTime }, the only two fields a
 *                     drag may write. `warning` is a non-blocking string or null.
 */
export function commitDecision({
  caseRow,
  roomCases = [],
  nextStartTime,
  nextEndTime,
  placed = [],
  roomNames = null,
} = {}) {
  // ── The refusal ────────────────────────────────────────────────────────────
  //
  // ⚠️ THIS DOES NOT CONTRADICT `doubleBooking.js:70` OR `AssignmentModal`.
  // `doubleBooking.js:70` deliberately ignores a same-room overlap, and
  // `AssignmentModal`'s second-case branch deliberately creates one — both are
  // about ADDING a case on purpose: a split case, or an add-on laid over the
  // original, entered by someone who means it and can see both rows.
  //
  // This is the opposite gesture: SLIDING AN EXISTING CASE ON TOP OF ANOTHER,
  // by accident, with a mouse, on a chart where the two bars then draw stacked
  // and read as one long case. Nobody has stated an intent, and the person doing
  // it cannot see what they just buried. Refusing here is consistent with both
  // of those — do NOT "fix" this by allowing the overlap to match them.
  const verdict = validateCaseTimes({
    startTime: nextStartTime,
    endTime: nextEndTime,
    otherCases: otherCasesIn(roomCases, caseRow),
  });
  if (!verdict.ok) {
    // Verbatim. The validator already names the colliding case's real times;
    // re-wording it here would eventually disagree with what the edit modal says
    // about the identical situation.
    return { ok: false, error: verdict.error, warning: null, patch: null };
  }

  // ── The warning ────────────────────────────────────────────────────────────
  //
  // ⚠️ NEVER BLOCKING. One person in two rooms at once is detected everywhere on
  // this board and blocked nowhere — the board's job is to be able to represent a
  // schedule that is wrong, so somebody can see it and fix it. A drag is not the
  // place to start refusing it. This is a warning exactly like
  // `coverageConsequence` (panelTimes.js:84).
  let warning = null;
  const key = keyOf(caseRow);
  if (key) {
    const nextStartMin = hmToMinutes(nextStartTime);
    const nextEndMin = hmToMinutes(nextEndTime);
    if (nextStartMin != null && nextEndMin != null) {
      const before = doubleBookedProviders(arr(placed));
      const after = doubleBookedProviders(hypotheticalBoard(placed, caseRow, nextStartMin, nextEndMin));
      const wasPaired = pairKeys(before, key);
      const conflict = arr(after).find((c) => c && c.key === key);
      // Only a NEW room pair is news. A clash that existed before the drag and
      // still exists after it was not created by this move, and re-announcing it
      // on every 15-minute nudge is how a warning gets ignored.
      const fresh = Array.from(pairKeys(after, key)).filter((p) => !wasPaired.has(p));
      if (conflict && fresh.length) {
        const rooms = new Set();
        for (const sig of fresh) for (const id of sig.split("|")) rooms.add(id);
        const who = nameOf(caseRow) || conflict.name || "That provider";
        warning = `${who} is now in ${labelRooms(Array.from(rooms), roomNames)} at the same time.`;
      }
    }
  }

  return {
    ok: true,
    error: null,
    warning,
    // Two fields, and only two. ⚠️ Do NOT extend this to the room doc:
    // `setRoomAssignment` hardcodes `status: "ready"` and would silently un-run a
    // running room, and `TimelineCaseModal.saveTimes` does not write it either.
    patch: { startTime: nextStartTime, endTime: nextEndTime },
  };
}
